/** Fixed memory; samples are existing complete-day wall timings, never a timer. */
export const RECENT_DAY_LIMIT = 120;
export interface RecentDayStats {
  limit: number; count: number; fromDay: number | null; toDay: number | null;
  meanMs: number | null; p50Ms: number | null; p95Ms: number | null; maxMs: number | null;
}
const validDay = (day: unknown): day is number => typeof day === 'number' && Number.isSafeInteger(day) && day >= 0 && day <= 0xffff_ffff;
const validMs = (ms: unknown): ms is number => typeof ms === 'number' && Number.isFinite(ms) && ms >= 0;
const empty = (): RecentDayStats => ({ limit: RECENT_DAY_LIMIT, count: 0, fromDay: null, toDay: null,
  meanMs: null, p50Ms: null, p95Ms: null, maxMs: null });

export class RecentDayWindow {
  private readonly days = new Uint32Array(RECENT_DAY_LIMIT);
  private readonly times = new Float64Array(RECENT_DAY_LIMIT);
  private count = 0;
  private next = 0;
  private lastDay: number | null = null;
  private cached: RecentDayStats | null = null;
  reset() { this.count = this.next = 0; this.lastDay = null; this.cached = null; }
  /** O(1), monotone complete days only; queries and pauses cannot add a sample. */
  record(day: number, ms: number): boolean {
    if (!validDay(day) || !validMs(ms) || (this.lastDay !== null && day <= this.lastDay)) return false;
    this.days[this.next] = day; this.times[this.next] = ms;
    this.next = (this.next + 1) % RECENT_DAY_LIMIT;
    this.count = Math.min(this.count + 1, RECENT_DAY_LIMIT);
    this.lastDay = day; this.cached = null;
    return true;
  }
  /** At most120 entries, only on snapshot demand; repeated reads reuse scalars. */
  snapshot(): RecentDayStats {
    if (!this.cached) {
      if (!this.count) this.cached = empty();
      else {
        const first = (this.next - this.count + RECENT_DAY_LIMIT) % RECENT_DAY_LIMIT;
        const values: number[] = [];
        let mean = 0;
        for (let i = 0; i < this.count; i++) {
          const ms = this.times[(first + i) % RECENT_DAY_LIMIT];
          values.push(ms); mean += (ms - mean) / (i + 1);
        }
        values.sort((a, b) => a - b);
        const rank = (p: number) => values[Math.ceil(p * values.length) - 1];
        this.cached = { limit: RECENT_DAY_LIMIT, count: this.count, fromDay: this.days[first], toDay: this.lastDay,
          meanMs: mean, p50Ms: rank(0.5), p95Ms: rank(0.95), maxMs: values.at(-1)! };
      }
    }
    return { ...this.cached }; // caller mutation must not corrupt the cached view
  }
}

/** Unknown/old Worker data is never reconstructed from cumulative counters. */
export function readRecentDays(value: unknown): RecentDayStats | null {
  if (!value || typeof value !== 'object') return null;
  const r = value as RecentDayStats;
  if (r.limit !== RECENT_DAY_LIMIT || !Number.isSafeInteger(r.count) || r.count < 0 || r.count > RECENT_DAY_LIMIT) return null;
  if (r.count === 0) return r.fromDay === null && r.toDay === null && r.meanMs === null && r.p50Ms === null && r.p95Ms === null && r.maxMs === null ? empty() : null;
  if (!validDay(r.fromDay) || !validDay(r.toDay) || r.fromDay > r.toDay || r.count > r.toDay - r.fromDay + 1) return null;
  if (![r.meanMs, r.p50Ms, r.p95Ms, r.maxMs].every(validMs)) return null;
  if (r.p50Ms! > r.p95Ms! || r.p95Ms! > r.maxMs! || r.meanMs! > r.maxMs!) return null;
  return { limit: r.limit, count: r.count, fromDay: r.fromDay, toDay: r.toDay,
    meanMs: r.meanMs, p50Ms: r.p50Ms, p95Ms: r.p95Ms, maxMs: r.maxMs };
}
